import React from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { StochasticDashboardFilters } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/DashboardFilters/StochasticDashboardFilters";
import { useStochasticDashboard } from "@/modules/stochastic/features/SalesAndCustomerDashboard/hooks/useStochasticDashboard";
import TotalSalesNewVsExistingCustomerChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalSalesNewVsExistingCustomerChart/TotalSalesNewVsExistingCustomerChart";
import RevenueByInvoiceAgeChart from "../Chart/RevenueByInvoiceAgeChart/RevenueByInvoiceAgeChart";
import TotalSalesNewCustomerByZipCodeAndYearChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalSalesNewCustomerByZipCodeAndYearChart/TotalSalesNewCustomerByZipCodeAndYearChart";
import TotalSalesByYearAndMonthChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalSalesByYearAndMonthChart/TotalSalesByYearAndMonthChart";
import TotalSalesByZipCodeChart from "../Chart/TotalSalesByZipCodeChart/TotalSalesByZipCodeChart";
import { PercentageOfNewCustomersByZipCodeChart } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/PercentageOfNewCustomersByZipCodeChart/PercentageOfNewCustomersByZipCodeChart";
import CustomersAverageInvoiceComparisonChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/CustomersAverageInvoiceComparisonChart/CustomersAverageInvoiceComparisonChart";
import LifetimeValueByTierChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/LifetimeValueByTierChart/LifetimeValueByTierChart";
import { PercentageOfNewCustomersChangeByZipCodeTable } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Table/PercentageOfNewCustomersChangeByZipCodeTable/PercentageOfNewCustomersChangeByZipCodeTable";
import { LifetimeValueByTierTable } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Table/LifetimeValueByTierTable/LifetimeValueByTierTable";
import LifetimeValueByTierChartSkeleton from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/LifetimeValueByTierChart/LifetimeValueByTierChartSkeleton";
import RevenueByInvoiceAgeChartSkeleton from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/RevenueByInvoiceAgeChart/RevenueByInvoiceAgeChartSkeleton";
import { PercentageOfNewCustomersByZipCodeChartSkeleton } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/PercentageOfNewCustomersByZipCodeChart/PercentageOfNewCustomersByZipCodeChartSkeleton";
import { TotalSalesByYearAndMonthChartSkeleton } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalSalesByYearAndMonthChart/TotalSalesByYearAndMonthChartSkeleton";
import { TotalSalesByZipCodeChartSkeleton } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalSalesByZipCodeChart/TotalSalesByZipCodeChartSkeleton";
import TotalSalesNewCustomerByZipCodeAndYearChartSkeleton from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalSalesNewCustomerByZipCodeAndYearChart/TotalSalesNewCustomerByZipCodeAndYearChartSkeleton";
import TotalSalesNewVsExistingCustomerChartSkeleton from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalSalesNewVsExistingCustomerChart/TotalSalesNewVsExistingCustomerChartSkeleton";
import { CustomersAverageInvoiceComparisonChartSkeleton } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/CustomersAverageInvoiceComparisonChart/CustomersAverageInvoiceComparisonChartSkeleton";
import { LifetimeValueByTierTableSkeleton } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Table/LifetimeValueByTierTable/LifetimeValueByTierTableSkeleton";
import { PercentageOfNewCustomersChangeByZipCodeTableSkeleton } from "../Table/PercentageOfNewCustomersChangeByZipCodeTable/PercentageOfNewCustomersChangeByZipCodeTableSkeleton";
import NewCustomerReplacementEfficiencyChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/NewCustomerReplacementEfficiencyChart/NewCustomerReplacementEfficiencyChart";
import NewCustomerReplacementEfficiencyChartSkeleton from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/NewCustomerReplacementEfficiencyChart/NewCustomerReplacementEfficiencyChartSkeleton";
import ExistingCustomerReplacementEfficiencyChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/ExistingCustomerReplacementEfficiencyChart/ExistingCustomerReplacementEfficiencyChart";
import ExistingCustomerReplacementEfficiencyChartSkeleton from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/ExistingCustomerReplacementEfficiencyChart/ExistingCustomerReplacementEfficiencyChartSkeleton";
import TotalHouseholdsByZipCodeChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalHouseholdsByZipCodeChart/TotalHouseholdsByZipCodeChart";
import { TotalHouseholdsByZipCodeChartSkeleton } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalHouseholdsByZipCodeChart/TotalHouseholdsByZipCodeChartSkeleton";
import { LastSyncStatus } from "@/modules/stochastic/features/shared/components/LastSyncStatus/LastSyncStatus";

export default function DashboardPage() {
  const {
    dashboardData,
    dashboardFiltersForm,
    loading,
    refreshing,
    activeTab,
    setActiveTab,
    applyFilters,
    areFiltersDirty,
    handleRefresh,
  } = useStochasticDashboard();

  const refreshButton = (
    <div className="flex items-center gap-3">
      <LastSyncStatus lastSync={dashboardData.lastSync} />
      <button
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
        disabled={refreshing}
        onClick={handleRefresh}
      >
        <ArrowPathIcon
          className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
        />
        Refresh
      </button>
    </div>
  );

  const renderSalesCharts = () => (
    <>
      <TotalSalesNewVsExistingCustomerChart
        initialData={dashboardData.totalSalesNewVsExistingCustomerData || []}
      />
      <TotalSalesByYearAndMonthChart
        initialData={dashboardData.totalSalesByYearAndMonthData}
      />
      <TotalSalesByZipCodeChart
        initialData={dashboardData.totalSalesByZipCodeData || []}
      />
      <TotalSalesNewCustomerByZipCodeAndYearChart
        initialData={dashboardData.totalSalesNewCustomerByZipCodeAndYearData}
      />
    </>
  );

  const renderSalesChartSkeletons = () => (
    <>
      <TotalSalesNewVsExistingCustomerChartSkeleton />
      <TotalSalesByYearAndMonthChartSkeleton />
      <TotalSalesByZipCodeChartSkeleton />
      <TotalSalesNewCustomerByZipCodeAndYearChartSkeleton />
    </>
  );

  const renderCustomerCharts = () => (
    <>
      <RevenueByInvoiceAgeChart
        initialData={dashboardData.revenueByInvoiceAgeData || []}
      />
      <LifetimeValueByTierChart
        initialData={dashboardData.lifetimeValueByTierData}
      />
      <LifetimeValueByTierTable
        initialData={dashboardData.lifetimeValueByTierData}
      />
      <PercentageOfNewCustomersByZipCodeChart
        initialData={dashboardData.percentageOfNewCustomersByZipCodeData}
      />
      <PercentageOfNewCustomersChangeByZipCodeTable
        initialData={
          dashboardData.percentageOfNewCustomersChangeByZipCodeData || []
        }
      />
      <CustomersAverageInvoiceComparisonChart
        initialData={dashboardData.customersAverageInvoiceComparisonData || []}
      />
      <NewCustomerReplacementEfficiencyChart
        initialData={dashboardData.newCustomerReplacementEfficiencyData || []}
        monthlyData={dashboardData.newCustomerReplacementEfficiencyByMonthData}
      />
      <ExistingCustomerReplacementEfficiencyChart
        initialData={
          dashboardData.existingCustomerReplacementEfficiencyData || []
        }
        monthlyData={
          dashboardData.existingCustomerReplacementEfficiencyByMonthData
        }
      />
    </>
  );

  const renderCustomerSkeletons = () => (
    <>
      <RevenueByInvoiceAgeChartSkeleton />
      <LifetimeValueByTierChartSkeleton />
      <LifetimeValueByTierTableSkeleton />
      <PercentageOfNewCustomersByZipCodeChartSkeleton />
      <PercentageOfNewCustomersChangeByZipCodeTableSkeleton />
      <CustomersAverageInvoiceComparisonChartSkeleton />
      <NewCustomerReplacementEfficiencyChartSkeleton />
      <ExistingCustomerReplacementEfficiencyChartSkeleton />
    </>
  );

  const renderReviewCharts = () => (
    <>
      <TotalSalesByYearAndMonthChart
        initialData={dashboardData.totalSalesByYearAndMonthData}
      />
      <RevenueByInvoiceAgeChart
        initialData={dashboardData.revenueByInvoiceAgeData || []}
      />
      <TotalSalesByZipCodeChart
        initialData={dashboardData.totalSalesByZipCodeData || []}
      />
      <TotalHouseholdsByZipCodeChart
        initialData={dashboardData.totalHouseholdsByZipCodeData}
      />
      <PercentageOfNewCustomersByZipCodeChart
        initialData={dashboardData.percentageOfNewCustomersByZipCodeData}
      />
      <CustomersAverageInvoiceComparisonChart
        initialData={dashboardData.customersAverageInvoiceComparisonData || []}
      />
      <NewCustomerReplacementEfficiencyChart
        initialData={dashboardData.newCustomerReplacementEfficiencyData || []}
        monthlyData={dashboardData.newCustomerReplacementEfficiencyByMonthData}
      />
      <ExistingCustomerReplacementEfficiencyChart
        initialData={
          dashboardData.existingCustomerReplacementEfficiencyData || []
        }
        monthlyData={
          dashboardData.existingCustomerReplacementEfficiencyByMonthData
        }
      />
      <LifetimeValueByTierChart
        initialData={dashboardData.lifetimeValueByTierData}
      />
    </>
  );

  const renderReviewChartSkeletons = () => (
    <>
      <TotalSalesByYearAndMonthChartSkeleton />
      <RevenueByInvoiceAgeChartSkeleton />
      <TotalSalesByZipCodeChartSkeleton />
      <TotalHouseholdsByZipCodeChartSkeleton />
      <PercentageOfNewCustomersByZipCodeChartSkeleton />
      <CustomersAverageInvoiceComparisonChartSkeleton />
      <NewCustomerReplacementEfficiencyChartSkeleton />
      <ExistingCustomerReplacementEfficiencyChartSkeleton />
      <LifetimeValueByTierChartSkeleton />
    </>
  );

  const renderChartSkeletons = () => {
    if (activeTab === "sales") return renderSalesChartSkeletons();
    if (activeTab === "customers") return renderCustomerSkeletons();
    return renderReviewChartSkeletons();
  };

  const renderCharts = () => {
    if (activeTab === "sales") return renderSalesCharts();
    if (activeTab === "customers") return renderCustomerCharts();
    return renderReviewCharts();
  };

  return (
    <MainPageWrapper
      actions={refreshButton}
      loading={loading}
      title="Sales and Customer Dashboard"
    >
      <div className="bg-white dark:bg-secondary-dark min-h-screen transition-colors duration-200">
        <StochasticDashboardFilters
          activeTab={activeTab}
          areFiltersDirty={areFiltersDirty}
          chartFiltersForm={dashboardFiltersForm}
          onApplyFilters={applyFilters}
          setActiveTab={setActiveTab}
        />
        <div className="max-w-7xl mx-auto space-y-8">
          {loading ? renderChartSkeletons() : renderCharts()}
        </div>
      </div>
    </MainPageWrapper>
  );
}
